import { useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import { VwStoricoAnnoDto, VwStoricoAnnoSearchDtoSortDirectionEnum } from '@api/client';
import { filtersDashboardStoricoUsedSelector } from '../../_redux/selectors';
import { getStoricoAnno } from '../../_redux/actions';

// Uno snapshot contiene ~2.500 progetti per misura: lo scarichiamo intero per poter confrontare due date
const SNAPSHOT_PAGE_SIZE = 5000;
// Se l'utente non sceglie una data, confrontiamo con la rilevazione di una settimana fa
const DEFAULT_GIORNI_CONFRONTO = 7;
// Chiave di cache per l'ultima rilevazione quando non è selezionata nessuna misura
const ALL_KEYS = '*';

// Misure gestite dal tab: AF_Ricavi, AF_Costi e AF_Margine _Anno_in_corso
export const CHIAVI_STORICO = [
  { id: '2', labelKey: 'dashboard.storico.filters.revenues' },
  { id: '8', labelKey: 'dashboard.storico.filters.costs' },
  { id: '20', labelKey: 'dashboard.storico.filters.margin' },
];

// All'apertura del tab è selezionata Revenues
export const DEFAULT_CHIAVE_STORICO = '2';

const chiaveOrder = (id?: string) => CHIAVI_STORICO.findIndex((c) => c.id === String(id));

export const getDefaultDataConfronto = () =>
  dayjs().subtract(DEFAULT_GIORNI_CONFRONTO, 'day').format('YYYY-MM-DD');

export interface StoricoConfrontoRow {
  id: string;
  fkProgetto?: number;
  fkProgettoIntranet?: string;
  progetto: string;
  fkChiaveMisura?: string;
  misura: string;
  valoreConfronto: number;
  valoreAttuale: number;
  gap: number;
  gapPerc: number | null;
}

export interface StoricoConfrontoTotali {
  misura: string;
  fkChiaveMisura?: string;
  valoreConfronto: number;
  valoreAttuale: number;
  gap: number;
  gapPerc: number | null;
}

const toIsoDate = (date: Date | string) => (typeof date === 'string' ? date : date.toISOString().substring(0, 10));

const calcGapPerc = (gap: number, confronto: number) => (confronto ? gap / Math.abs(confronto) : null);

// Senza filtro chiave ogni progetto ha una riga per misura: il join va fatto su progetto + misura
const rowKey = (r: VwStoricoAnnoDto) => `${r.fkProgetto}|${r.fkChiaveMisura}`;

const useStoricoConfronto = () => {
  const dispatch = useAppDispatch();
  const filtersUsed = useAppSelector(filtersDashboardStoricoUsedSelector);

  const [rows, setRows] = useState<StoricoConfrontoRow[]>([]);
  const [totali, setTotali] = useState<StoricoConfrontoTotali[]>([]);
  const [dataAttuale, setDataAttuale] = useState<string | null>(null);
  const [dataConfronto, setDataConfronto] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [noConfrontoSnapshot, setNoConfrontoSnapshot] = useState<boolean>(false);
  const latestDateCache = useRef(new Map<string, Date | string>());

  const search = useCallback(
    (fkChiaveMisura: string | undefined, dataSnapshot: string | undefined, page: number, size: number) =>
      dispatch(
        getStoricoAnno({
          vwStoricoAnnoSearchDto: {
            fkChiaveMisura,
            dataSnapshot: dataSnapshot ? new Date(dataSnapshot) : undefined,
            sortDirection: VwStoricoAnnoSearchDtoSortDirectionEnum.Desc,
            paging: { page, size },
          },
        })
      ).unwrap(),
    [dispatch]
  );

  const fetchSnapshot = useCallback(
    async (fkChiaveMisura: string | undefined, dataSnapshot: string) => {
      const first = await search(fkChiaveMisura, dataSnapshot, 0, SNAPSHOT_PAGE_SIZE);
      const content = [...(first?.content || [])];
      for (let page = 1; page < (first?.totalPages || 0); page++) {
        const next = await search(fkChiaveMisura, dataSnapshot, page, SNAPSHOT_PAGE_SIZE);
        content.push(...(next?.content || []));
      }
      return content;
    },
    [search]
  );

  const clear = () => {
    setRows([]);
    setTotali([]);
    setDataAttuale(null);
    setDataConfronto(null);
  };

  const load = useCallback(
    async (fkChiaveMisura?: string, dataConfrontoScelta?: string) => {
      setIsLoading(true);
      setNoConfrontoSnapshot(false);
      try {
        // L'ultima rilevazione disponibile è il valore "attuale" (query lenta: la teniamo in cache)
        const cacheKey = fkChiaveMisura ?? ALL_KEYS;
        let latestDate: Date | string | undefined = latestDateCache.current.get(cacheKey);
        if (!latestDate) {
          const latest = await search(fkChiaveMisura, undefined, 0, 1);
          latestDate = latest?.content?.[0]?.dataSnapshot;
          if (latestDate) latestDateCache.current.set(cacheKey, latestDate);
        }
        if (!latestDate) {
          clear();
          return;
        }
        // L'attuale è oggi; se la rilevazione di oggi non c'è ancora, l'ultima disponibile
        const attuale = toIsoDate(latestDate);
        const confronto = dataConfrontoScelta || getDefaultDataConfronto();

        // Senza chiave il BE restituisce tutte le misure: teniamo solo quelle gestite dal tab
        const isChiaveStorico = (r: VwStoricoAnnoDto) =>
          CHIAVI_STORICO.some((c) => c.id === String(r.fkChiaveMisura));
        const [snapshotAttuale, snapshotConfronto] = (
          await Promise.all([fetchSnapshot(fkChiaveMisura, attuale), fetchSnapshot(fkChiaveMisura, confronto)])
        ).map((snapshot) => snapshot.filter(isChiaveStorico));

        // Senza rilevazione alla data di confronto i gap sarebbero falsati (confronto sempre a 0)
        if (snapshotConfronto.length === 0) {
          clear();
          setNoConfrontoSnapshot(true);
          return;
        }

        // Join per progetto + misura: una riga assente in uno dei due snapshot vale 0
        const byKey = new Map<string, { info: VwStoricoAnnoDto; attuale: number; confronto: number }>();
        snapshotAttuale.forEach((r) => byKey.set(rowKey(r), { info: r, attuale: r.stValore || 0, confronto: 0 }));
        snapshotConfronto.forEach((r) => {
          const existing = byKey.get(rowKey(r));
          if (existing) existing.confronto = r.stValore || 0;
          else byKey.set(rowKey(r), { info: r, attuale: 0, confronto: r.stValore || 0 });
        });

        const merged: StoricoConfrontoRow[] = Array.from(byKey.entries()).map(([id, { info, attuale, confronto }]) => {
          const gap = attuale - confronto;
          return {
            id,
            fkProgetto: info.fkProgetto,
            fkProgettoIntranet: info.fkProgettoIntranet,
            progetto: `(${info.fkProgettoIntranet}) ${info.denominazioneProgetto}`,
            fkChiaveMisura: info.fkChiaveMisura,
            misura: info.denominazioneMisura || '-',
            valoreConfronto: confronto,
            valoreAttuale: attuale,
            gap,
            gapPerc: calcGapPerc(gap, confronto),
          };
        });

        // Totali separati per misura: sommare misure diverse non avrebbe senso
        const totaliByMisura = new Map<string, StoricoConfrontoTotali>();
        merged.forEach((r) => {
          const tot = totaliByMisura.get(r.misura) || {
            misura: r.misura,
            fkChiaveMisura: r.fkChiaveMisura,
            valoreConfronto: 0,
            valoreAttuale: 0,
            gap: 0,
            gapPerc: null,
          };
          tot.valoreConfronto += r.valoreConfronto;
          tot.valoreAttuale += r.valoreAttuale;
          totaliByMisura.set(r.misura, tot);
        });
        const totaliList = Array.from(totaliByMisura.values())
          .map((tot) => {
            const gap = tot.valoreAttuale - tot.valoreConfronto;
            return { ...tot, gap, gapPerc: calcGapPerc(gap, tot.valoreConfronto) };
          })
          // Stesso ordine dei pulsanti: Revenues, Costs, Margin
          .sort((a, b) => chiaveOrder(a.fkChiaveMisura) - chiaveOrder(b.fkChiaveMisura));

        setRows(merged);
        setTotali(totaliList);
        setDataAttuale(attuale);
        setDataConfronto(confronto);
      } catch {
        clear();
      } finally {
        setIsLoading(false);
      }
    },
    [search, fetchSnapshot]
  );

  // La chiave è facoltativa: senza filtro si confrontano tutte le misure
  // Prima di qualsiasi ricerca vale la chiave di default; dopo una ricerca senza pulsante attivo, tutte le misure
  useEffect(() => {
    load(filtersUsed ? filtersUsed.fkChiaveMisura : DEFAULT_CHIAVE_STORICO, filtersUsed?.dataConfronto);
  }, [filtersUsed, load]);

  return { rows, totali, dataAttuale, dataConfronto, isLoading, noConfrontoSnapshot };
};

export default useStoricoConfronto;
